// 状態を持つモーダルを扱うユーティリティ

const modalStatus = Symbol('modalStatus');
const MODAL_CLOSED = Symbol('MODAL_CLOSED');
const MODAL_OPEN = Symbol('MODAL_OPEN');
const MODAL_CLOSING = Symbol('MODAL_CLOSING');

export type ModalStateClosed = {
  [modalStatus]: typeof MODAL_CLOSED;
};
export type ModalStateOpen<T = unknown> = {
  [modalStatus]: typeof MODAL_OPEN;
  data: T;
};

export type ModalStateClosing<T = unknown> = {
  [modalStatus]: typeof MODAL_CLOSING;
  data: T;
};

export type ModalDataExists<T> = ModalStateOpen<T> | ModalStateClosing<T>;

export type ModalState<T = unknown> = ModalStateClosed | ModalStateOpen<T> | ModalStateClosing<T>;

export const isModalOpen = <T = unknown>(state: ModalState<T>): state is ModalStateOpen<T> => {
  return state[modalStatus] === MODAL_OPEN;
};

export const isModalClosed = <T = unknown>(state: ModalState<T>): state is ModalStateClosed => {
  return state[modalStatus] === MODAL_CLOSING || state[modalStatus] === MODAL_CLOSED;
};

export const isModalDataExists = <T = unknown>(state: ModalState<T>): state is ModalDataExists<T> => {
  return state[modalStatus] === MODAL_CLOSING || state[modalStatus] === MODAL_OPEN;
};

export const modalStateClosed: ModalStateClosed = {
  [modalStatus]: MODAL_CLOSED,
};

export const toModalClosing = <T = unknown>(state: ModalState<T>): ModalState<T> => {
  if (isModalDataExists(state)) {
    return {
      [modalStatus]: MODAL_CLOSING,
      data: state.data,
    };
  }
  return state;
};

export const modalStateOpen = <T = unknown>(data: T): ModalStateOpen<T> => {
  return {
    [modalStatus]: MODAL_OPEN,
    data,
  };
};

export const getModalData = <T = unknown>(state: ModalDataExists<T>): T => {
  return state.data;
};

export const unwrapModalData = <T = unknown>(state: ModalState<T>): T | null => {
  if (isModalDataExists(state)) {
    return state.data;
  }
  return null;
};

export const foldModal = <R, T = unknown>(state: ModalState<T>, onClosed: () => R, onOpen: (data: T) => R): R => {
  if (isModalDataExists(state)) {
    return onOpen(state.data);
  }
  return onClosed();
};
